import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Radar, Newspaper, Database, Search, Eye, Wrench, ChevronDown, Download, Monitor, Apple, Server, Smartphone, Globe } from 'lucide-react';
import { api } from '../lib/api';
import { PricingBlock } from './pricing-block';

/* ================= 工具 ================= */
function formatTokens(n: number): string {
  if (!n || n === 0) return '-';
  if (n >= Number.MAX_SAFE_INTEGER) return '∞';
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

/** 数字滚动 */
function useCountUp(target: number | null, duration = 900): number {
  const [val, setVal] = useState(0);
  const raf = useRef<number>(0);
  useEffect(() => {
    if (target === null) return;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      setVal(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return val;
}

/** 安全渲染自定义 HTML：剥离 script/事件属性/javascript: 协议 */
export function sanitizeHtml(raw: string): string {
  return String(raw || '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/(href|src)\s*=\s*["']?\s*javascript:/gi, '$1="#"');
}

/** 实时统计区块数据 */
function useLiveStats() {
  const [stats, setStats] = useState<{ totalModels: number; availableModels: number; totalVendors: number; totalFreeTokens: number } | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () => {
      fetch(api('/api/catalog/stats')).then(r => r.json()).then(d => { if (alive) setStats(d); }).catch(() => {});
    };
    load();
    const timer = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(timer); };
  }, []);
  return stats;
}

const FEATURE_ICONS: Record<string, any> = { bot: Bot, radar: Radar, news: Newspaper, db: Database };

/* ================= 区块类型定义（编辑器与渲染器共用） ================= */
export const BLOCK_TYPES: { type: string; label: string; desc: string; system?: boolean }[] = [
  { type: 'hero', label: '主视觉 Hero', desc: '大标题 + 副标题 + 徽标 + 双按钮' },
  { type: 'stats', label: '实时统计', desc: '模型数 / 厂商数 / 免费额度动态数字' },
  { type: 'features', label: '特性卡片', desc: '图标 + 标题 + 描述的卡片组' },
  { type: 'text', label: '标题段落', desc: '标题 + 段落文本' },
  { type: 'cta', label: '行动号召', desc: '按钮横幅，引导用户操作' },
  { type: 'faq', label: '问答折叠', desc: '可折叠的问题列表' },
  { type: 'image', label: '图片', desc: '图片 + 说明文字' },
  { type: 'divider', label: '分隔线', desc: '区块间隔装饰线' },
  { type: 'html', label: '自定义 HTML', desc: '自由 HTML/CSS（管理员专用，脚本被自动剥离）' },
  { type: 'download', label: '软件下载', desc: '客户端安装包列表（后台下载中心上传）' },
  { type: 'models', label: '模型目录（系统）', desc: '实时模型搜索与表格', system: true },
  { type: 'pricing', label: '定价与兑换（系统）', desc: '订阅购买 + 兑换码激活', system: true },
];

export function newBlock(type: string): any {
  const id = `b-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  switch (type) {
    case 'hero': return { id, type, label: 'HERO', title: '新的标题', subtitle: '副标题说明文字', badge: 'SECTION TAG', primaryText: '主要按钮', primaryLink: '/models', secondaryText: '', secondaryLink: '' };
    case 'stats': return { id, type, label: 'LIVE STATS', title: '', subtitle: '' };
    case 'features': return { id, type, label: 'FEATURES', title: '核心功能', subtitle: '', items: [{ icon: 'bot', title: '特性一', desc: '特性描述文字' }, { icon: 'radar', title: '特性二', desc: '特性描述文字' }] };
    case 'text': return { id, type, label: 'TEXT', title: '章节标题', content: '在这里编写段落内容……' };
    case 'cta': return { id, type, label: 'CTA', title: '行动号召标题', text: '辅助说明文字', primaryText: '按钮文字', primaryLink: '/', secondaryText: '', secondaryLink: '' };
    case 'faq': return { id, type, label: 'FAQ', title: '常见问题', subtitle: '', items: [{ q: '问题一？', a: '回答内容……' }, { q: '问题二？', a: '回答内容……' }] };
    case 'image': return { id, type, label: 'IMAGE', url: '', caption: '图片说明', width: 'full' };
    case 'divider': return { id, type, label: 'DIVIDER', text: '' };
    case 'html': return { id, type, label: 'HTML', html: '<div style="text-align:center;padding:24px;border:1px dashed #5ef2e0;color:#94a3b8;">自定义 HTML 区块</div>' };
    case 'download': return { id, type, label: 'DOWNLOAD', title: '客户端下载', subtitle: '选择适合你系统的客户端版本' };
    case 'models': return { id, type, label: 'MODEL DATABASE', title: '模型目录', subtitle: '' };
    case 'pricing': return { id, type, label: 'PRICING', title: '定价方案', subtitle: '为 CXAI-FreeRouter 提供实时目录数据同步' };
    default: return { id, type, label: type.toUpperCase(), title: '', subtitle: '' };
  }
}

/* ================= 模型目录区块（从 ModelsPage 抽取） ================= */
const MODEL_STATUS_META: Record<string, { label: string; dot: string }> = {
  available: { label: '可用', dot: 'on' },
  unavailable: { label: '不可用', dot: 'off' },
  rate_limited: { label: '限流', dot: 'mid' },
  unknown: { label: '未知', dot: 'mid' },
};

interface Model {
  platform: string; model_id: string; display_name: string;
  category: string; capabilities: string[]; context_window: number | null;
  free_quota: number; free_quota_label: string; availability_status: string;
  intelligence_rank: number; supports_vision: boolean; supports_tools: boolean;
  sync_visibility: string;
  vendor_name: string; quality_score: number; quality_source: string; quality_updated_at: string;
}
interface Vendor { id: string; name: string; }

function ModelsTableBlock({ title, subtitle }: { title?: string; subtitle?: string }) {
  const [models, setModels] = useState<Model[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetch(api('/api/catalog/vendors')).then(r => r.json()).then((rows: any[]) => setVendors(rows.map((v: any) => ({ id: v.id, name: v.name })))).catch(() => setVendors([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterPlatform) params.set('platform', filterPlatform);
    if (filterStatus) params.set('status', filterStatus);
    if (search) params.set('search', search);
    fetch(api(`/api/catalog/models?${params}`))
      .then(r => r.json())
      .then(data => { setModels(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filterPlatform, filterStatus, search]);

  return (
    <div className="max-w-7xl mx-auto px-6 pt-14 pb-24">
      <div className="mb-10">
        <div className="hud-label mb-3 flex items-center gap-3">
          <span className="status-dot on" /> Model Database · Live
        </div>
        <h1 className="font-display font-bold text-[clamp(2.2rem,4.5vw,3.6rem)] tracking-tight leading-tight">
          {title || '模型目录'}<span className="gradient-text">_</span>
        </h1>
        {subtitle && <p className="mt-3 text-[var(--ink-dim)]">{subtitle}</p>}
      </div>

      <div className="mb-6 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3">
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ink-dim)]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="搜索模型名称、ID、厂商..." className="sci-input w-full pl-11 pr-4 py-3" />
        </div>
        <select value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)} className="sci-input sci-select px-4 py-3 cursor-pointer">
          <option value="">所有厂商（{vendors.length}）</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="sci-input sci-select px-4 py-3 cursor-pointer">
          <option value="">所有状态</option>
          <option value="available">可用</option>
          <option value="unknown">未知</option>
        </select>
      </div>

      <div className="hud-label mb-4">{loading ? '扫描中…' : `${String(models.length).padStart(3, '0')} RECORDS FOUND`}</div>

      {loading ? (
        <div className="glass-card scanline flex flex-col items-center justify-center gap-4 py-24">
          <div className="status-dot on !h-3 !w-3" />
          <div className="hud-label">正在同步模型数据库…</div>
        </div>
      ) : models.length === 0 ? (
        <div className="glass-card py-24 text-center">
          <div className="hud-label mb-3">NO RECORDS</div>
          <p className="text-sm text-[var(--ink-dim)]">未找到匹配的模型，试试调整筛选条件</p>
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--line)] bg-[rgba(255,255,255,0.02)]">
                  <th className="hud-label py-3.5 pl-6 text-left !text-[0.62rem]">状态</th>
                  <th className="hud-label py-3.5 text-left !text-[0.62rem]">模型</th>
                  <th className="hud-label py-3.5 text-left !text-[0.62rem]">厂商</th>
                  <th className="hud-label py-3.5 text-left !text-[0.62rem]">类别</th>
                  <th className="hud-label py-3.5 text-right !text-[0.62rem]">免费额度</th>
                  <th className="hud-label py-3.5 text-right !text-[0.62rem]">上下文</th>
                  <th className="hud-label py-3.5 text-center !text-[0.62rem]">评分</th>
                  <th className="hud-label py-3.5 text-center !text-[0.62rem]">能力</th>
                </tr>
              </thead>
              <tbody>
                {models.map((m, i) => {
                  const st = MODEL_STATUS_META[m.availability_status] || MODEL_STATUS_META.unknown;
                  const qs = Number(m.quality_score) || 0;
                  return (
                    <tr key={i} className="group border-b border-[var(--line)]/60 transition-colors hover:bg-[rgba(94,242,224,0.045)]" style={{ borderLeft: '2px solid transparent' }}
                      onMouseEnter={e => (e.currentTarget.style.borderLeftColor = 'var(--accent)')}
                      onMouseLeave={e => (e.currentTarget.style.borderLeftColor = 'transparent')}>
                      <td className="py-3.5 pl-6"><span className={`status-dot ${st.dot}`} title={st.label} /></td>
                      <td className="py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="font-display font-medium text-[var(--ink)]">{m.display_name}</div>
                          {m.sync_visibility === 'paid' && (
                            <span className="rounded-[6px] border border-[rgba(250,204,21,0.4)] bg-[rgba(250,204,21,0.1)] px-1.5 py-0.5 text-[0.62rem] font-semibold tracking-wider text-[#facc15]" title="该模型仅付费订阅用户可通过客户端同步获取（免费版客户端拿不到）">VIP</span>
                          )}
                        </div>
                        <div className="font-mono text-xs text-[var(--ink-dim)]">{m.model_id}</div>
                      </td>
                      <td className="py-3.5 font-mono text-xs text-[var(--ink-dim)]">{m.vendor_name || m.platform}</td>
                      <td className="py-3.5"><span className="rounded-[6px] border border-[var(--line)] bg-[rgba(255,255,255,0.04)] px-2 py-1 font-mono text-xs text-[var(--ink-dim)]">{m.category}</span></td>
                      <td className="py-3.5 text-right font-mono text-xs font-medium text-[var(--accent)]">{m.free_quota_label || formatTokens(m.free_quota)}</td>
                      <td className="py-3.5 text-right font-mono text-xs text-[var(--ink-dim)]">{m.context_window ? formatTokens(m.context_window) : '-'}</td>
                      <td className="py-3.5 text-center">
                        {qs > 0 ? (
                          <span className="inline-flex items-center gap-1 rounded-[6px] border border-[rgba(250,204,21,0.3)] bg-[rgba(250,204,21,0.08)] px-2 py-1 text-xs font-medium text-[#facc15]" title={`来源: ${m.quality_source || '未知'} · 更新: ${m.quality_updated_at || '-'}`}>
                            {'★'.repeat(Math.round(qs))}<span className="text-[#facc15]/40">{'☆'.repeat(5 - Math.round(qs))}</span>
                          </span>
                        ) : (
                          <span className="text-[var(--ink-dim)]/60 text-xs" title="订阅后由客户端同步获取精准评分">订阅解锁</span>
                        )}
                      </td>
                      <td className="py-3.5">
                        <div className="flex items-center justify-center gap-1.5">
                          {m.supports_vision && <span className="inline-flex items-center gap-1 rounded-[6px] border border-[rgba(139,124,246,0.35)] bg-[rgba(139,124,246,0.1)] px-2 py-1 text-[0.68rem] text-[var(--accent-2)]"><Eye size={11} /> 视觉</span>}
                          {m.supports_tools && <span className="inline-flex items-center gap-1 rounded-[6px] border border-[var(--line-strong)] bg-[rgba(94,242,224,0.08)] px-2 py-1 text-[0.68rem] text-[var(--accent)]"><Wrench size={11} /> 工具</span>}
                          {!m.supports_vision && !m.supports_tools && <span className="text-[var(--ink-dim)]/50 text-xs">—</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= 单个区块渲染 ================= */
const PLATFORM_META: Record<string, { label: string; Icon: any }> = {
  windows: { label: 'Windows', Icon: Monitor },
  macos: { label: 'macOS', Icon: Apple },
  linux: { label: 'Linux', Icon: Server },
  android: { label: 'Android', Icon: Smartphone },
  ios: { label: 'iOS', Icon: Globe },
  other: { label: '其他', Icon: Globe },
};

function formatBytes(n: number): string {
  if (!n) return '-';
  if (n >= 1024 * 1024 * 1024) return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}

/** 软件下载区块：列出后台下载中心上传的客户端安装包 */
function DownloadsBlock({ block }: { block: any }) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetch(api('/api/catalog/downloads'))
      .then(r => (r.ok ? r.json() : []))
      .then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);
  return (
    <section className="max-w-5xl mx-auto px-6 py-16 w-full">
      {block.title && <h2 className="sci-h2 text-center mb-2">{block.title}<span className="tick">_</span></h2>}
      {block.subtitle && <p className="text-center text-[var(--ink-dim)] mb-8">{block.subtitle}</p>}
      {loading ? (
        <div className="flex justify-center py-10"><div className="status-dot on" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--line)] py-12 text-center text-[var(--ink-dim)]">暂无可用下载</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
          {items.map(d => {
            const meta = PLATFORM_META[d.platform] || PLATFORM_META.other;
            const Icon = meta.Icon;
            return (
              <div key={d.id} className="glass-card p-5 flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-[var(--line-strong)] bg-[rgba(94,242,224,0.08)] text-[var(--accent)]">
                  <Icon size={20} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-semibold text-[var(--ink)] truncate">{d.name}</span>
                    <span className="hud-label !text-[0.6rem]">{d.version}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-[var(--ink-dim)]">
                    {meta.label} · {formatBytes(d.file_size)}
                    {d.file_hash && <span className="ml-2 text-[0.62rem] text-[var(--ink-dim)]/60" title={d.file_hash}>SHA-256 校验</span>}
                  </div>
                  {d.description && <p className="mt-1.5 text-xs text-[var(--ink-dim)]/90 line-clamp-2">{d.description}</p>}
                </div>
                <a
                  href={api(`/api/catalog/downloads/${d.id}/file`)}
                  className="neon-btn shrink-0 px-4 py-2 !text-xs"
                  title={d.file_name}
                >
                  <Download size={14} className="mr-1 inline" />下载
                </a>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function StatsBlock({ block }: { block: any }) {
  const stats = useLiveStats();
  const models = useCountUp(stats?.totalModels ?? null);
  const vendors = useCountUp(stats?.totalVendors ?? null);
  const tokens = useCountUp(stats?.totalFreeTokens ?? null);
  return (
    <section className="max-w-3xl mx-auto px-6 py-16">
      {block.title && <h2 className="sci-h2 text-center mb-8">{block.title}<span className="tick">_</span></h2>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
        <div className="glass-card relative px-6 py-5 text-center md:text-left">
          <div className="hud-label mb-2">Model Count</div>
          <div className="font-display text-3xl md:text-4xl font-bold tracking-tight gradient-text">{stats ? String(models) : '···'}</div>
          <div className="absolute inset-x-6 bottom-0 h-px bg-gradient-to-r from-transparent via-[var(--line-strong)] to-transparent" />
        </div>
        <div className="glass-card relative px-6 py-5 text-center md:text-left">
          <div className="hud-label mb-2">Vendors</div>
          <div className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[var(--ink)]">{stats ? String(vendors) : '···'}</div>
          <div className="absolute inset-x-6 bottom-0 h-px bg-gradient-to-r from-transparent via-[var(--line-strong)] to-transparent" />
        </div>
        <div className="glass-card relative px-6 py-5 text-center md:text-left">
          <div className="hud-label mb-2">Free Tokens / Month</div>
          <div className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[var(--ink)]">{stats ? formatTokens(tokens) : '···'}</div>
          <div className="absolute inset-x-6 bottom-0 h-px bg-gradient-to-r from-transparent via-[var(--line-strong)] to-transparent" />
        </div>
      </div>
    </section>
  );
}

function FaqBlock({ block }: { block: any }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <section className="max-w-3xl mx-auto px-6 py-14">
      <div className="text-center mb-10">
        {block.label && <div className="hud-label mb-3">{block.label}</div>}
        {block.title && <h2 className="sci-h2">{block.title}<span className="tick">_</span></h2>}
        {block.subtitle && <p className="mt-3 text-[var(--ink-dim)]">{block.subtitle}</p>}
      </div>
      <div className="space-y-3">
        {(block.items || []).map((it: any, i: number) => (
          <div key={i} className="glass-card overflow-hidden">
            <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between px-5 py-4 text-left">
              <span className="font-display text-base font-medium text-[var(--ink)]">{it.q}</span>
              <ChevronDown size={16} className={`shrink-0 text-[var(--accent)] transition-transform ${open === i ? 'rotate-180' : ''}`} />
            </button>
            {open === i && <div className="px-5 pb-5 text-sm text-[var(--ink-dim)] leading-relaxed whitespace-pre-wrap">{it.a}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

function RenderBlock({ block }: { block: any }) {
  switch (block.type) {
    case 'hero': {
      return (
        <section className="relative min-h-[72vh] flex flex-col items-center justify-center overflow-hidden text-center px-6">
          <div className="absolute inset-0 -z-10">
            <img src="/hero-bg.jpg" alt="" className="h-full w-full object-cover opacity-60" />
            <div className="absolute inset-0 bg-[radial-gradient(1000px_600px_at_50%_38%,rgba(7,13,28,0.25),rgba(7,13,28,0.92)_78%)]" />
            <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[var(--canvas)] to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[var(--canvas)] to-transparent" />
          </div>
      {false && <span className="sr-only">hero background</span>}
          {block.badge && (
            <div className="hud-label mb-5 flex items-center gap-3 anim-fade-up">
              <span className="status-dot on" />
              <span className="tracking-[0.2em]">{block.badge}</span>
              <span className="status-dot on" />
            </div>
          )}
          {block.title && (
            <h1 className="font-display font-bold leading-[1.05] tracking-tight text-[clamp(2.6rem,6.5vw,5.5rem)] anim-fade-up" style={{ animationDelay: '0.08s' }}>
              <span className="gradient-text">{block.title}</span>
            </h1>
          )}
          {block.subtitle && (
            <p className="mt-6 max-w-2xl text-base md:text-lg text-[var(--ink-dim)] leading-relaxed anim-fade-up" style={{ animationDelay: '0.16s' }}>
              {block.subtitle}
            </p>
          )}
          {(block.primaryText || block.secondaryText) && (
            <div className="mt-10 flex flex-col sm:flex-row items-center gap-4 anim-fade-up" style={{ animationDelay: '0.32s' }}>
              {block.primaryText && <Link to={block.primaryLink || '/'} className="neon-btn px-9 py-3.5 text-base">{block.primaryText}</Link>}
              {block.secondaryText && <Link to={block.secondaryLink || '/'} className="ghost-btn px-9 py-3.5 text-base">{block.secondaryText}</Link>}
            </div>
          )}
        </section>
      );
    }
    case 'stats': {
      return <StatsBlock block={block} />;
    }
    case 'features': {
      const items = block.items || [];
      const [first, ...rest] = items;
      return (
        <section className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center mb-12 anim-fade-up">
            {block.label && <div className="hud-label mb-3">{block.label}</div>}
            {block.title && <h2 className="sci-h2">{block.title}<span className="tick">_</span></h2>}
            {block.subtitle && <p className="mt-3 text-[var(--ink-dim)]">{block.subtitle}</p>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {first && (
              <div className="glass-card md:col-span-2 relative overflow-hidden p-8 md:p-10 scanline">
                <div className="absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-[rgba(94,242,224,0.07)] to-transparent" />
                <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[var(--line-strong)] bg-[rgba(94,242,224,0.1)] text-[var(--accent)] shadow-[var(--shadow-glow)] anim-float">
                    {(() => { const I = FEATURE_ICONS[first.icon] || Bot; return <I size={26} strokeWidth={1.8} />; })()}
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-semibold tracking-tight mb-2">{first.title}</h3>
                    <p className="text-sm md:text-base text-[var(--ink-dim)] leading-relaxed max-w-2xl">{first.desc}</p>
                  </div>
                </div>
              </div>
            )}
            {rest.map((f: any, i: number) => (
              <div key={i} className="glass-card group p-7 transition-all duration-200 hover:border-[var(--line-strong)] hover:shadow-[var(--shadow-glow)]">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-[var(--line)] bg-[rgba(255,255,255,0.04)] text-[var(--accent)] transition-colors group-hover:bg-[rgba(94,242,224,0.1)]">
                  {(() => { const I = FEATURE_ICONS[f.icon] || Bot; return <I size={21} strokeWidth={1.8} />; })()}
                </div>
                <h3 className="font-display text-lg font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-[var(--ink-dim)] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>
      );
    }
    case 'text':
      return (
        <section className="max-w-4xl mx-auto px-6 py-14">
          {block.title && <h2 className="sci-h2 mb-6">{block.title}<span className="tick">_</span></h2>}
          <div className="prose-sci text-[var(--ink-dim)] leading-relaxed whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.content || '') }} />
        </section>
      );
    case 'cta':
      return (
        <section className="max-w-4xl mx-auto px-6 py-16">
          <div className="glass-card scanline relative overflow-hidden p-10 md:p-14 text-center">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--accent)] to-transparent" />
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4">{block.title}</h2>
            {block.text && <p className="text-[var(--ink-dim)] mb-8 max-w-xl mx-auto">{block.text}</p>}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {block.primaryText && <Link to={block.primaryLink || '/'} className="neon-btn px-9 py-3.5">{block.primaryText}</Link>}
              {block.secondaryText && <Link to={block.secondaryLink || '/'} className="ghost-btn px-9 py-3.5">{block.secondaryText}</Link>}
            </div>
          </div>
        </section>
      );
    case 'faq': {
      return <FaqBlock block={block} />;
    }
    case 'image':
      return (
        <section className="max-w-4xl mx-auto px-6 py-14 text-center">
          {block.url ? (
            <img src={block.url} alt={block.caption || ''} className="mx-auto max-h-[70vh] w-auto rounded-xl border border-[var(--line)] object-contain" />
          ) : (
            <div className="mx-auto flex h-52 max-w-2xl items-center justify-center rounded-xl border-2 border-dashed border-[var(--line)] text-[var(--ink-dim)]">图片待配置（在右侧面板填写图片 URL）</div>
          )}
          {block.caption && <p className="mt-3 hud-label">{block.caption}</p>}
        </section>
      );
    case 'divider':
      return (
        <div className="max-w-5xl mx-auto px-6 py-8">
          {block.text ? (
            <div className="flex items-center gap-4">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[var(--line-strong)] to-transparent" />
              <span className="hud-label">{block.text}</span>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[var(--line-strong)] to-transparent" />
            </div>
          ) : (
            <div className="h-px w-full bg-gradient-to-r from-transparent via-[var(--line-strong)] to-transparent" />
          )}
        </div>
      );
    case 'html':
      return <section className="px-6 py-14"><div className="mx-auto max-w-5xl" dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.html || '') }} /></section>;
    case 'models':
      return <ModelsTableBlock title={block.title} subtitle={block.subtitle} />;
    case 'pricing':
      return <PricingBlock title={block.title} subtitle={block.subtitle} />;
    case 'download':
      return <DownloadsBlock block={block} />;
    default:
      return <div className="py-10 text-center text-[var(--ink-dim)]">未知区块类型：{block.type}</div>;
  }
}

/* ================= 页面区块渲染器（前台与编辑器共用，保证所见即所得） ================= */
export function PageBlocks({ blocks, selectedId, onSelect, onMove, onDelete, onDuplicate, onPatch }: {
  blocks: any[];
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  onMove?: (id: string, dir: -1 | 1) => void;
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onPatch?: (id: string, patch: any) => void;
}) {
  const editing = !!onSelect;
  return (
    <div>
      {blocks.map((b) => {
        const inner = <RenderBlock block={b} />;
        if (!editing) return <div key={b.id}>{inner}</div>;
        return (
          <div
            key={b.id}
            onClick={() => onSelect!(b.id)}
            className={`group relative cursor-pointer transition-all ${selectedId === b.id ? 'ring-2 ring-[var(--accent)] ring-offset-2 ring-offset-[var(--canvas)]' : 'hover:ring-1 hover:ring-[rgba(94,242,224,0.35)]'}`}
          >
            {selectedId === b.id && (
              <div className="absolute right-3 top-3 z-20 flex items-center gap-1 rounded-lg border border-[var(--line)] bg-[#0d1428]/95 px-1.5 py-1 shadow-lg" onClick={e => e.stopPropagation()}>
                <button onClick={() => onMove!(b.id, -1)} title="上移" className="px-1.5 text-xs text-[var(--ink-dim)] hover:text-[var(--accent)]">↑</button>
                <button onClick={() => onMove!(b.id, 1)} title="下移" className="px-1.5 text-xs text-[var(--ink-dim)] hover:text-[var(--accent)]">↓</button>
                <button onClick={() => onDuplicate!(b.id)} title="复制" className="px-1.5 text-xs text-[var(--ink-dim)] hover:text-[var(--accent)]">⧉</button>
                <button onClick={() => onDelete!(b.id)} title="删除" className="px-1.5 text-xs text-[#ff5d7a] hover:text-red-400">✕</button>
              </div>
            )}
            {inner}
          </div>
        );
      })}
      {blocks.length === 0 && <div className="py-24 text-center text-[var(--ink-dim)]">页面暂无区块，从左侧组件面板添加</div>}
    </div>
  );
}
